import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';

interface Issue {
  id: string; booking_id: number; type: string; priority: 'high' | 'medium' | 'low';
  title: string; detail: string; name: string; account: string; amount: number; date: string;
}
interface Result { issues: Issue[]; booking_count: number; checked_at: string; }
interface AIResult {
  model: string; notice: string; assessment: string;
  suggestion: { category_name: string; confidence: number; needs_review: boolean; reason: string; };
}
const priorities = { high: 'Hoch', medium: 'Mittel', low: 'Niedrig' };
const types: Record<string, string> = {
  category_sum: 'Kategoriesumme', missing_category: 'Kategorie fehlt', missing_receipt: 'Ausgabe ohne Beleg',
  receipt_sum: 'Beleg ↔ Buchung', receipt_categories: 'Beleg ↔ Kategorien',
  receipt_unclear: 'Belege manuell klären', invalid_amount: 'Ungültiger Betrag',
  recurring_receipt: 'Wiederkehrend · Dauerbeleg prüfen',
  bank_fee_receipt: 'Bankabschluss · Kontoauszug prüfen',
};

export default function KassenpruefungIssues({ year, onFilter }: {
  year: number; onFilter: (ids: number[] | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [priority, setPriority] = useState('all');
  const [type, setType] = useState('all');
  const [activeFilter, setActiveFilter] = useState(false);
  const [limit, setLimit] = useState(20);
  const [ai, setAI] = useState<Record<number, AIResult>>({});
  const [aiErrors, setAIErrors] = useState<Record<number, string>>({});
  const [aiLoading, setAILoading] = useState<number | null>(null);
  const request = useRef(0);

  const load = async () => {
    const current = ++request.current;
    setLoading(true); setError('');
    try {
      const response = await apiClient.get(`/api/kassenpruefung/${year}/issues`);
      if (current !== request.current) return;
      if (!response.data.success) throw new Error();
      setResult(response.data); setAI({}); setAIErrors({});
    } catch {
      if (current === request.current) {
        setResult(null); setError('Prüfhinweise konnten nicht geladen werden.');
      }
    } finally { if (current === request.current) setLoading(false); }
  };
  useEffect(() => {
    load();
    return () => { request.current++; };
    // The parent remounts this component for each year.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const filtered = (result?.issues || []).filter(issue =>
    (priority === 'all' || issue.priority === priority) && (type === 'all' || issue.type === type));
  const bookingIds = Array.from(new Set(filtered.map(issue => issue.booking_id)));
  const filterKey = bookingIds.join(',');
  useEffect(() => {
    onFilter(activeFilter && result ? bookingIds : null);
    // Stable IDs describe the complete filter; avoid depending on the recreated array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, filterKey, result, onFilter]);

  const review = async (id: number) => {
    const current = request.current;
    setAILoading(id); setAIErrors(prev => ({ ...prev, [id]: '' }));
    try {
      const response = await apiClient.post(`/api/kassenpruefung/${year}/transactions/${id}/ai-review`);
      if (!response.data.success) throw new Error();
      if (current === request.current) setAI(prev => ({ ...prev, [id]: response.data }));
    } catch {
      if (current === request.current) setAIErrors(prev => ({ ...prev, [id]: 'KI-Prüfung nicht verfügbar. Du kannst die Buchung weiterhin manuell prüfen.' }));
    } finally { if (current === request.current) setAILoading(null); }
  };
  const selectClass = 'rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800';

  return <section className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432] text-gray-800 dark:text-gray-100" aria-label="Automatische Prüfhilfe">
    <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="audit-issues-content"
      className="flex w-full items-center gap-3 p-4 text-left focus-visible:ring-2 focus-visible:ring-primary-500 rounded-2xl">
      <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-amber-600 dark:text-amber-400" />
      <span className="min-w-0 flex-1"><span className="block text-sm font-semibold">Automatische Prüfhilfe</span>
        <span className="block text-xs text-gray-500 dark:text-gray-400">
          {loading ? 'Buchungen werden geprüft …' : error || (result ? `${result.issues.length} Hinweise · ${new Set(result.issues.map(i => i.booking_id)).size} betroffene Buchungen · ${result.issues.filter(i => i.priority === 'high').length} hohe Priorität` : 'Noch nicht geprüft')}
          {activeFilter && ' · Buchungsfilter aktiv'}
        </span></span>
      <ChevronDownIcon className={`h-5 w-5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && <div id="audit-issues-content" className="space-y-4 border-t border-gray-200 p-4 dark:border-gray-700">
      <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">Jede reguläre Buchung braucht mindestens eine Kategorie; die Anteile müssen zusammen den Buchungsbetrag ergeben. Umbuchungen sind ausgenommen. Hinweise sind keine Beanstandungen. Mehrere Belege werden gemeinsam geprüft; unklare Zuordnungen bleiben zur manuellen Klärung offen.</p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="grid gap-1 text-xs">Problemtyp<select aria-label="Problemtyp" className={selectClass} value={type} onChange={e => { setType(e.target.value); setLimit(20); }}>
          <option value="all">Alle Problemtypen</option>{Object.entries(types).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select></label>
        <label className="grid gap-1 text-xs">Priorität<select aria-label="Priorität" className={selectClass} value={priority} onChange={e => { setPriority(e.target.value); setLimit(20); }}>
          <option value="all">Alle Prioritäten</option>{Object.entries(priorities).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select></label>
        <button type="button" className="btn ghost sm" disabled={loading || aiLoading !== null} onClick={load}>{loading ? 'Prüft …' : 'Neu prüfen'}</button>
        <label className="flex min-h-10 items-center gap-2 text-xs"><input type="checkbox" checked={activeFilter} disabled={!result} onChange={e => setActiveFilter(e.target.checked)} />Buchungsliste auf diese Treffer filtern</label>
      </div>
      {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      {result && <p className="text-xs text-gray-500 dark:text-gray-400">{filtered.length} Hinweise zu {bookingIds.length} Buchungen · {result.booking_count} Buchungen geprüft · Stand {new Date(result.checked_at).toLocaleString('de-DE')}</p>}
      {result && !filtered.length && <p className="text-sm">Keine Hinweise für diese Auswahl. Das ersetzt keine abschließende Kassenprüfung.</p>}
      <div className="space-y-3">{filtered.slice(0, limit).map(issue => <article key={issue.id} className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-2"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${issue.priority === 'high' ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300' : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300'}`}>{priorities[issue.priority]}</span><h3 className="text-sm font-semibold">{issue.title}</h3></div>
        <p className="mt-2 text-sm break-words">{issue.name} · {issue.amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{new Date(issue.date).toLocaleDateString('de-DE')} · {issue.account} · Buchung #{issue.booking_id}</p>
        <p className="mt-2 text-xs leading-5">{issue.detail}</p>
        <button type="button" className="btn ghost sm mt-2" disabled={aiLoading !== null || loading} onClick={() => review(issue.booking_id)}>{aiLoading === issue.booking_id ? 'KI prüft …' : 'KI-Kategorie prüfen'}</button>
        {aiErrors[issue.booking_id] && <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">{aiErrors[issue.booking_id]}</p>}
        {ai[issue.booking_id] && <div className="mt-2 rounded-lg bg-gray-50 p-3 text-xs leading-5 dark:bg-gray-800">
          <strong>KI-Vorschlag: {ai[issue.booking_id].suggestion.category_name} · {ai[issue.booking_id].suggestion.confidence} %</strong>
          <p>{ai[issue.booking_id].assessment === 'matching' ? 'Entspricht der vorhandenen Kategorie.' : ai[issue.booking_id].assessment === 'different' ? 'Weicht von der vorhandenen Kategorie ab; bitte prüfen.' : 'Mehrere Kategorien vorhanden.'}</p>
          <p>{ai[issue.booking_id].suggestion.reason}</p><p>{ai[issue.booking_id].notice}</p><p>Modell: {ai[issue.booking_id].model}</p>
        </div>}
      </article>)}</div>
      {filtered.length > limit && <button type="button" className="btn ghost sm" onClick={() => setLimit(v => v + 20)}>Weitere Hinweise ({filtered.length - limit})</button>}
      <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">Die Summenprüfung läuft ohne KI. „KI-Kategorie prüfen“ verwendet die konfigurierte KI-Anbindung wie im Buchungsdetail (JEV bevorzugt, lokaler Modus bleibt lokal) und kann Kosten verursachen. Keine automatische Änderung oder Freigabe.</p>
    </div>}
  </section>;
}
